import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Home,
  FileText,
  CalendarCheck,
  Car,
  MapPin,
  BusFront,
  Phone,
  Tag,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../images/aombre-logo.png";
import us from "../images/us1.png";
import es from "../images/es1.png";
import fr from "../images/fr1.png";

const languages = [
  { code: "EN", label: "ENGLISH", flag: us, path: "/" },
  {
    code: "ES",
    label: "SPANISH",
    flag: es,
    path: "https://spanish.aombre.com/",
  },
  { code: "FR", label: "FRENCH", flag: fr, path: "https://french.aombre.com/" },
];

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedLang, setSelectedLang] = useState(languages[0]);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const navigate = useNavigate();
  const location = useLocation(); // We use this to check the active path
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (!isHomePage) return;
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  // Logic for desktop navbar background, applying styles from medium breakpoint and up
  const desktopNavbarBgClass = menuOpen
    ? "md:bg-[#006039] md:shadow-md"
    : isHomePage
    ? isScrolled
      ? "md:bg-[#006039] md:backdrop-blur-md md:shadow-md"
      : "md:bg-transparent md:shadow-none" // On desktop homepage (top), it's transparent
    : "md:bg-[#006039] md:shadow-md"; // On other pages, it's always dark

  const menuItems = [
    { label: "Home", path: "/", icon: <Home size={18} /> },
    // { label: "Shuttle Tracker", path: null, icon: <BusFront size={18} /> },
    {
      label: "Reservations",
      path: "/bookings",
      icon: <CalendarCheck size={18} />,
    },
    { label: "Vehicles", path: "/vehicles", icon: <Car size={18} /> },
    { label: "Locations", path: "/locations", icon: <MapPin size={18} /> },
    { label: "Offers", path: "/offers", icon: <Tag size={18} /> },
    {
      label: "Rental Policy",
      path: "/rental-policy",
      icon: <FileText size={18} />,
    },
    { label: "Contact Us", path: "/contact", icon: <Phone size={18} /> },
  ];

  
  const handleClick = (item) => {
    if (item.label === "Shuttle Tracker") {
      setShowPopup(true);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleLanguageChange = (lang) => {
    setSelectedLang(lang);
    if (lang.code === "ES" || lang.code === "FR") {
      window.location.href = lang.path;
    } else {
      navigate(lang.path);
    }
    setShowLangMenu(false);
  };

  return (
    <nav
      className={`px-6 py-4 sticky top-0 z-50 transition-colors duration-300 bg-[#006039] shadow-md ${desktopNavbarBgClass}`}
      style={{
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      <div className="flex items-center justify-between text-white max-w-7xl mx-auto">
        {/* Logo */}
        <img
          src={logo}
          alt="Logo"
          className="w-24 md:w-48 h-auto cursor-pointer"
          onClick={() => navigate("/")}
        />

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">
          {/* --- NAV LINKS (FONT SIZE INCREASED) --- */}
          <button
            onClick={() => navigate("/")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Home
          </button>
          <button
            onClick={() => navigate("/locations")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/locations"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Locations
          </button>
          <button
            onClick={() => navigate("/vehicles")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/vehicles"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Vehicles
          </button>
          <button
            onClick={() => navigate("/contact")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/contact"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Contact Us
          </button>
          <button
            onClick={() => navigate("/bookings")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/bookings"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Reservation
          </button>
          <button
            onClick={() => navigate("/rental-policy")}
            // --- CHANGED HERE ---
            className={`text-base font-medium px-3 py-1 rounded hover:bg-white/10 transition uppercase border-b-2 ${
              location.pathname === "/rental-policy"
                ? "border-[#ca8032]"
                : "border-transparent"
            }`}
          >
            Rental Policy
          </button>

          {/* 🌍 Language Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center space-x-2 px-3 py-1 rounded hover:bg-white/10 transition"
            >
              <img
                src={selectedLang.flag}
                alt="flag"
                className="w-5 h-5 rounded"
              />
              <span className="text-sm font-medium">{selectedLang.label}</span>
            </button>

            {showLangMenu && (
              <div className="absolute right-0 mt-2 bg-white text-[#006039] rounded shadow-lg w-36 z-50">
                {languages.map((lang) => (
                  <div
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang)}
                    className="flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100"
                  >
                    <img
                      src={lang.flag}
                      alt={lang.label}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm">{lang.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden flex items-center space-x-4">
          {/* Language with Flag + Initials */}
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center space-x-1 px-2 py-1 rounded hover:bg-white/10 transition"
            >
              <img
                src={selectedLang.flag}
                alt="flag"
                className="w-5 h-5 rounded"
              />
              <span className="text-xs font-medium">{selectedLang.code}</span>
            </button>

            {showLangMenu && (
              <div className="absolute right-0 mt-2 bg-white text-[#006039] rounded shadow-lg w-28 z-50">
                {languages.map((lang) => (
                  <div
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang)}
                    className="flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100"
                  >
                    <img
                      src={lang.flag}
                      alt={lang.label}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-xs">{lang.code}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-[#006039] text-white z-50 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-end p-4">
          <button onClick={() => setMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>
        <div className="flex flex-col items-start px-6 space-y-4 mt-4">
          {menuItems.map((item, idx) => (
            <div
              key={idx}
              // --- THIS ALREADY USES THE CORRECT COLOR ---
              className={`flex items-center space-x-2 cursor-pointer hover:text-[#ca8032] transition ${
                location.pathname === item.path ? "text-[#ca8032]" : ""
              }`}
              onClick={() => {
                setMenuOpen(false);
                handleClick(item);
              }}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full text-center m-2">
            <h2 className="text-2xl text-[#006039] font-bold mb-2">
              🚧 In Progress
            </h2>
            <p className="text-gray-600 mb-4">
              This feature is coming soon. Stay tuned!
            </p>
            <button
              onClick={() => setShowPopup(false)}
              className="px-4 py-2 bg-[#006039] text-white rounded hover:bg-[#ca8032] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
